import React, { useContext } from 'react';
import {
  IUIUrval,
  UIUrvalConditionObjectType,
  INumericConditionObject,
  IStringConditionObject,
  IUrvalCondition,
} from '../../Types/UrvalTypes';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IClientAttributeModel } from '../../Types/ClientModelTypes';
import { ModelContext } from '../../Model/ModelContext/ModelContext';
import { getKontodelList, conditionHasValue } from '../Urval/Utils/urvalUtils';
import mergeSort, { stringCompareNumeric } from '../../Utils/mergeSort';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import EDTooltip from '../../BaseComponents/Tooltip/EDTooltip';
interface IProps {
  kodbehorigheturval: IUIUrval[];
  isReserv?: Boolean;
}

interface IToolTipText {
  toolTipString: string;
  conditionString: string;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      koddel: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
      },
      koddelValuesContainer: {
        margin: '0 ' + theme.spacing(5.5) + ' 0 ' + theme.spacing(1),
      },
      koddelContainer: {
        display: 'flex',
        width: '100%',
      },
      conditionOperation: {
        margin: '0 ' + theme.spacing(1),
      },
      pageContainer: {
        marginTop: theme.spacing(2),
      },
      urvalContainer: {
        borderBottom: theme.customProperties.MenuList.listBorder,
        paddingBottom: theme.spacing(1),
      },
      infoTextContainer: {
        display: 'inline-block',
      },
      koddelValue: {
        display: 'inline-block',
        margin: '0 0 0 ' + theme.spacing(0.5),
      },
      toolTipContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        cursor: 'default',
      },
    } as const)
);

const getObject = (
  o: UIUrvalConditionObjectType
): INumericConditionObject | IStringConditionObject | null => {
  return o.type === 'Numeric'
    ? (o as INumericConditionObject)
    : o.type === 'String'
    ? (o as IStringConditionObject)
    : null;
};

const renderConditionOperation = (
  attribute: IRenderableAttribute
): IToolTipText => {
  let result: IToolTipText = { toolTipString: '', conditionString: '' };

  // Om value och toValue saknas skriv inte ut något...
  if (attribute.conditions.length > 0) {
    if (
      (attribute.conditions[0].values.length > 0 &&
        attribute.conditions[0].values[0].value != undefined) ||
      attribute.conditions[0].values[0].toValue != undefined
    ) {
      switch (attribute.conditions[0].op) {
        // copNotInterval
        case 'notinterval':
          result.toolTipString = 'Utanför intervall';
          result.conditionString = '!A-B';
          break;

        //copNotEqual
        case 'notequals':
          result.toolTipString = 'Inte lika med';
          result.conditionString = '!A';
          break;

        // copGreater
        case 'greater':
          result.toolTipString = 'Större än';
          result.conditionString = '>A';
          break;

        // copGreaterEqual
        case 'notless':
          result.toolTipString = 'Större än eller lika med';
          result.conditionString = '>=A';
          break;

        // copLessEqual
        case 'notgreater':
          result.toolTipString = 'Mindre än eller lika med';
          result.conditionString = '<=A';
          break;

        // copLess
        case 'less':
          result.toolTipString = 'Mindre än';
          result.conditionString = '<A';
          break;

        // copIsNull
        case 'isnull':
          result.toolTipString = 'Saknar värde';
          result.conditionString = '-';
          break;

        // copHasValue
        case 'hasvalue':
          result.toolTipString = 'Har värde';
          result.conditionString = '=?';
          break;

        //copStartingWith
        case 'starts':
          result.toolTipString = 'Börjar på';
          result.conditionString = 'A...';
          break;

        //copNotStartingWith
        case 'notstarts':
          result.toolTipString = 'Börjar inte på';
          result.conditionString = '!A...';
          break;
        //copLike
        case 'contains':
          result.toolTipString = 'Innehåller';
          result.conditionString = '..A..';
          break;
        //copNotLike
        case 'notcontains':
          result.toolTipString = 'Innehåller inte';
          result.conditionString = '!..A..';
          break;

        default:
          break;
      }
    }
  }

  return result;
};

const renderAttribute = (attribute: IRenderableAttribute, classes: any) => {
  const name = attribute.attribute.DisplayLabel;
  const toolTipObject = renderConditionOperation(attribute);
  return (
    <div key={name} className={classes.koddel}>
      <div className={classes.koddelContainer}>
        <EDTypography bold>{name}:</EDTypography>
        <EDTooltip title={toolTipObject.toolTipString}>
          <div className={classes.toolTipContainer}>
            <div className={classes.koddelValuesContainer}>
              {attribute.conditions.map(c =>
                c.values.map((x, index, arr) => {
                  return (
                    <>
                      <EDTypography
                        key={'value_' + index}
                        className={classes.koddelValue}
                      >
                        {x.value && ` ${x.value}`}
                        {x.toValue && ` - ${x.toValue}`}
                      </EDTypography>
                      {index + 1 !== arr.length && ','}
                    </>
                  );
                })
              )}
            </div>
            {toolTipObject.conditionString && (
              <EDTypography>{`(${toolTipObject.conditionString})`}</EDTypography>
            )}
          </div>
        </EDTooltip>
      </div>
    </div>
  );
};

interface IRenderableAttribute {
  attribute: IClientAttributeModel;
  conditions: IUrvalCondition[];
}

/**
 * Listning av information om kontodelsvärden på kodbehörighetsurvalet
 */
const KodbehorighetInfo: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles();
  const modelContext = useContext(ModelContext);
  const koddelar = getKontodelList(modelContext.avenyCollectionMap);

  return (
    <div>
      {props.kodbehorigheturval.map((urval, urvalIndex) => {
        return (
          <div
            key={urval.savedUrvalProps?.id}
            className={cx({
              [classes.urvalContainer]:
                urvalIndex < props.kodbehorigheturval.length - 1,
            })}
          >
            {urval.pages.map((p, pageIndex) => {
              const missingKoddelar = koddelar.filter(
                koddel =>
                  koddel.AttributeName.startsWith('KONTODEL_') &&
                  !p.conditionObjects.some(o => {
                    const object = getObject(o);
                    if (object) {
                      return (
                        object.attribute.AttributeName === koddel.AttributeName
                      );
                    } else {
                      return false;
                    }
                  })
              );

              const rendables = mergeSort(
                [
                  ...missingKoddelar.map((k): IRenderableAttribute => {
                    return { attribute: k, conditions: [] };
                  }),
                  ...p.conditionObjects
                    .map(getObject)
                    .map((o): IRenderableAttribute | null => {
                      if (
                        !o ||
                        (!conditionHasValue(o) && !o.id.startsWith('KONTODEL_'))
                      ) {
                        return null;
                      } else {
                        return {
                          attribute: o.attribute,
                          conditions: o.condition,
                        };
                      }
                    }),
                ],
                (a, b) => {
                  if (a === null) {
                    return -1;
                  }
                  if (b === null) {
                    return 1;
                  }

                  return stringCompareNumeric(
                    a.attribute.AttributeName,
                    b.attribute.AttributeName
                  );
                }
              );

              const severalSelection = props.kodbehorigheturval.length > 1;
              const writeKodBehorighetTitle =
                severalSelection || props.isReserv;

              return (
                <div
                  key={p.id}
                  className={classes.pageContainer}
                  data-testid="kodbehContainer"
                >
                  {writeKodBehorighetTitle && pageIndex === 0 && (
                    <EDTypography variant="subtitle1" bold testId="kodbehName">
                      {urval.savedUrvalProps?.namn}
                    </EDTypography>
                  )}
                  {urval.pages.length > 1 && (
                    <EDTypography
                      variant={
                        writeKodBehorighetTitle ? 'subtitle2' : 'subtitle1'
                      }
                      bold
                      testId="kodbehPage"
                    >
                      {p.pageName ?? `Sida ${pageIndex + 1}`}
                    </EDTypography>
                  )}
                  {rendables.map(r => {
                    if (!r) {
                      return null;
                    }

                    return renderAttribute(r, classes);
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default KodbehorighetInfo;
